import { useTheme } from '@mui/material';
import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Animates the composer dock between two heights for size changes that are not a live drag
 * (auto-size restore, keyboard steps). Call the returned function right before the state
 * change that resizes the dock.
 *
 * CSS can't transition to the dock's content-driven `fit-content` height outside Chromium, so
 * the dock is measured before and after the change and the gap is played with the Web
 * Animations API; min-height rides along, or a growing dock would jump straight to its floor.
 */
export function useDockHeightTransition(dockRef: RefObject<HTMLElement | null>): () => void {
  const theme = useTheme();
  // Read only in the layout effect of the very render it schedules; holding it in state
  // would cost an extra render and lose the pre-change measurement.
  const fromHeightRef = useRef<number | null>(null);
  const [scheduledCount, setScheduledCount] = useState(0);

  // Layout effect: the new height has to be read and the animation started before paint, or
  // the dock shows its final size for one frame and then jumps back to animate.
  useLayoutEffect(() => {
    const el = dockRef.current;
    const from = fromHeightRef.current;
    fromHeightRef.current = null;
    if (!el || from === null) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const to = el.getBoundingClientRect().height;
    if (Math.abs(to - from) < 1) return;

    el.animate(
      [
        { height: `${from}px`, minHeight: `${from}px` },
        { height: `${to}px`, minHeight: `${to}px` },
      ],
      {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeInOut,
      },
    );
  }, [scheduledCount, dockRef, theme]);

  return useCallback(() => {
    const el = dockRef.current;
    if (!el) return;
    // measured first: mid-animation, the current frame is where the next one has to start
    fromHeightRef.current = el.getBoundingClientRect().height;
    el.getAnimations().forEach((animation) => animation.cancel());
    setScheduledCount((count) => count + 1);
  }, [dockRef]);
}
